import {
  ORDER_HISTORY_DEFAULT_PAGE_SIZE,
  STARTING_PAGE_NUMBER,
} from '../../constants/pagination';

// when PaginationInput was deprecated with Swan v3, we had to move to a new style of showing pages.
// These helper functions are lifted from Gallery https://gitlab.com/vistaprint-org/gallery/gallery6-client/-/blob/master/src/client/components/Gallery/Pagination/PaginationSteps.tsx?ref_type=heads#L19

/**
 * generate an array of numbers from a integer range
 */
export const getPageRange = (start: number, stop: number): number[] =>
  Array.from({ length: (stop - start) / 1 + 1 }, (_k, v) => start + v * 1);

/**
 * Returns an array of three consecutive page numbers, which will be displayed between first and last page numbers
 * All logic here is based on page numbers, not page index.
 */
export function generatePageNumbers(
  currentPage: number,
  totalPageCount: number
): number[] {
  if (totalPageCount <= 2) {
    return [];
  }

  let pageNumbers: number[];

  // Don't count the first and last page
  const remainingPages =
    Math.min(ORDER_HISTORY_DEFAULT_PAGE_SIZE, totalPageCount) - 2;

  // Set the range of consecutive pages from the starting page, and from the ending page to see if the current page falls within either.
  // If it falls within the starting range, return the next three pages after the starting page, ie [2,3,4];
  // => PaginationSteps will display 1,2,3,4 ... <last page link>;

  // If it falls within the ending range, return an array of the the last three page numbers before the last page;
  // Example: for 10 pages, return [7,8,9] => PaginationSteps will display 1 ... 7,8,9,10.

  // Otherwise display three pages centered around the current page.
  // Example: return [5,6,7] => Pagination Steps will display 1 ... 5, 6, 7 ... <last page link>

  // leftOffset => greatest page number in starting range
  // This is based on constants, always evaluates to 4.
  const leftOffset = STARTING_PAGE_NUMBER + remainingPages;

  // right offset => lowest page number in ending range
  // example: for 10 total pages, this evaluates to 7, ie ending range is 7,8,9,10
  const rightOffset = totalPageCount - remainingPages;

  if (
    // current page number is 4 or more, ie not in starting range
    currentPage >= leftOffset &&
    // also not in ending range
    currentPage <= rightOffset &&
    leftOffset !== rightOffset
  ) {
    pageNumbers = [currentPage - 1, currentPage, currentPage + 1];
  } else if (leftOffset > currentPage) {
    // current page is in starting range
    pageNumbers = getPageRange(STARTING_PAGE_NUMBER + 1, leftOffset);
  } else {
    // current page is in ending range
    pageNumbers = getPageRange(rightOffset, totalPageCount - 1);
  }

  return pageNumbers;
}

/**
 * When there are more than five total pages, and you are on pages 1-3, we display 1,2,3,4 ... <last page link>.
 * Once you get to page 4, we have to decide which 5 page numbers to display.
 * We may revisit this pattern when / if we implement filtering orders by date, etc.
 * For now, to be consistent with pagination as implemented in Gallery, we have decided for now to display 1 ... current page-1, current page, current page + 1 ... last page.
 * Example for six pages: 1 ... 3, 4, 5, 6.
 * Example for 7 or more pages: 1 ... 3, 4, 5 ... 7
 * */
export const showEllipses = (
  totalPageCount: number,
  position: string,
  currentPage?: number,
  pageNumbers?: number[]
): boolean => {
  if (position === 'left' && currentPage) {
    // Based on constants, greatestPageNumberInStartingRange always evaluates to 4.
    // Instead of hard-coding that value, here we make it relative to constants, so it will change dynamically when they do.
    const greatestPageNumberInStartingRange =
      STARTING_PAGE_NUMBER + ORDER_HISTORY_DEFAULT_PAGE_SIZE - 2;
    return (
      currentPage >= greatestPageNumberInStartingRange &&
      totalPageCount > ORDER_HISTORY_DEFAULT_PAGE_SIZE
    );
  }

  if (position === 'right' && pageNumbers && pageNumbers.length) {
    const lastPageNumberInRange = pageNumbers[pageNumbers.length - 1];
    return (
      lastPageNumberInRange! < totalPageCount - 1 &&
      totalPageCount > ORDER_HISTORY_DEFAULT_PAGE_SIZE
    );
  }

  return false;
};
